import { FC } from 'react';

type ColorKey =
	| 'background'
	| 'surface'
	| 'surface_muted'
	| 'primary'
	| 'primary_foreground'
	| 'text'
	| 'text_muted'
	| 'border'
	| 'input'
	| 'input_border'
	| 'badge'
	| 'badge_text'
	| 'danger'
	| 'success';

interface Props {
	colors: Record<ColorKey, string>;
}

/**
 * Превью темы: как витрина магазина будет выглядеть у покупателя.
 *
 * Собрано так, чтобы каждый из четырнадцати цветов был где-то видно. Это не
 * педантизм: превью, в котором часть цветов не участвует, врёт — оператор
 * выбирает значение, не понимая, на что оно влияет, и узнаёт об этом уже от
 * продавца. Поэтому здесь есть и поле ввода, и бейдж, и обе служебные
 * подписи — успех и ошибка.
 *
 * Пропорции нарочно мелкие: это макет, а не пиксель-в-пиксель копия
 * витрины. Задача — увидеть сочетание цветов и поймать нечитаемое, вроде
 * белого текста на белом фоне.
 */
export const ThemePreview: FC<Props> = ({ colors }) => {
	const card = (title: string, price: string, inStock: boolean) => (
		<div
			style={{
				background: colors.surface,
				border: `1px solid ${colors.border}`,
				borderRadius: 8,
				overflow: 'hidden',
			}}
		>
			{/* Место под фото товара — самый большой участок приглушённой
			    поверхности, по нему её и оценивают. */}
			<div
				style={{
					background: colors.surface_muted,
					height: 44,
					position: 'relative',
				}}
			>
				<span
					style={{
						position: 'absolute',
						top: 4,
						left: 4,
						background: colors.badge,
						color: colors.badge_text,
						fontSize: 9,
						lineHeight: 1,
						padding: '3px 6px',
						borderRadius: 4,
					}}
				>
					ХИТ
				</span>
			</div>

			<div style={{ padding: 6 }}>
				<div style={{ color: colors.text, fontSize: 11, fontWeight: 600 }}>
					{title}
				</div>
				<div
					style={{
						color: inStock ? colors.success : colors.danger,
						fontSize: 9,
						marginTop: 2,
					}}
				>
					{inStock ? 'В наличии' : 'Нет в наличии'}
				</div>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						marginTop: 6,
					}}
				>
					<span style={{ color: colors.text, fontSize: 12, fontWeight: 700 }}>
						{price}
					</span>
					<span
						style={{
							background: colors.primary,
							color: colors.primary_foreground,
							fontSize: 9,
							padding: '4px 8px',
							borderRadius: 5,
						}}
					>
						Купить
					</span>
				</div>
			</div>
		</div>
	);

	return (
		<div
			style={{
				background: colors.background,
				border: `1px solid ${colors.border}`,
				borderRadius: 10,
				padding: 10,
			}}
		>
			<div style={{ color: colors.text, fontSize: 13, fontWeight: 700 }}>
				Мой магазин
			</div>
			<div style={{ color: colors.text_muted, fontSize: 10, marginTop: 1 }}>
				12 товаров · доставка сразу
			</div>

			{/* Поле поиска: единственное место, где видно цвета инпута. */}
			<div
				style={{
					background: colors.input,
					border: `1px solid ${colors.input_border}`,
					borderRadius: 6,
					padding: '6px 8px',
					marginTop: 8,
					color: colors.text_muted,
					fontSize: 10,
				}}
			>
				Поиск по товарам
			</div>

			<div
				style={{
					display: 'grid',
					gridTemplateColumns: '1fr 1fr',
					gap: 8,
					marginTop: 8,
				}}
			>
				{card('Аккаунт Steam', '$12.00', true)}
				{card('Ключ Windows', '$4.50', false)}
			</div>
		</div>
	);
};
